import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { LogOut, Plus, Trash2, Edit2, X, Check, Users, CalendarDays, Mail, Trophy, Newspaper, Image, Handshake, Camera, ShoppingBag, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ImageUpload from "@/components/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PWC_LOGO = "/pwc-logo-secondary.png";

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("pwc_token", data.token);
      onLogin(data.user);
      toast.success("Welcome back, boss.");
    } catch {
      toast.error("Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-foreground flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <img src={PWC_LOGO} alt="PWC" className="h-20 invert" />
        </div>
        <h1 data-testid="admin-login-title" className="font-heading text-2xl text-background text-center uppercase mb-8">
          Front Office
        </h1>
        <form onSubmit={handleLogin} data-testid="admin-login-form" className="space-y-4">
          <Input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email"
            className="rounded-none bg-background/10 border-none text-background placeholder:text-background/40 font-body"
          />
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            className="rounded-none bg-background/10 border-none text-background placeholder:text-background/40 font-body"
          />
          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full rounded-none bg-primary text-foreground hover:bg-primary/80 font-heading uppercase h-12 text-base border-none"
          >
            {loading ? "..." : "Enter"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function WrestlersPanel({ token }) {
  const [wrestlers, setWrestlers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", nickname: "", weight_class: "", hometown: "", bio: "",
    wins: 0, losses: 0, image_url: "", is_champion: false, championship_title: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  const loadWrestlers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/wrestlers`);
      setWrestlers(data);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadWrestlers(); }, [loadWrestlers]);

  const resetForm = () => {
    setForm({ name: "", nickname: "", weight_class: "", hometown: "", bio: "",
      wins: 0, losses: 0, image_url: "", is_champion: false, championship_title: "" });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/wrestlers/${editing}`, form, { headers });
        toast.success("Wrestler updated");
      } else {
        await axios.post(`${API}/wrestlers`, form, { headers });
        toast.success("Wrestler added");
      }
      resetForm();
      loadWrestlers();
    } catch { toast.error("Save failed"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/wrestlers/${id}`, { headers });
      toast.success("Wrestler removed");
      loadWrestlers();
    } catch { toast.error("Delete failed"); }
  };

  const startEdit = (w) => {
    setForm({
      name: w.name, nickname: w.nickname || "", weight_class: w.weight_class || "",
      hometown: w.hometown || "", bio: w.bio || "", wins: w.wins || 0, losses: w.losses || 0,
      image_url: w.image_url || "", is_champion: w.is_champion || false,
      championship_title: w.championship_title || ""
    });
    setEditing(w.id);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl uppercase">Roster ({wrestlers.length})</h2>
        <Button
          data-testid="add-wrestler-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 border-none"
        >
          <Plus size={14} className="mr-1" /> Add Wrestler
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-none border-none bg-background max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl">{editing ? "Edit Wrestler" : "Add Wrestler"}</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Fill in the wrestler details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="wrestler-name-input" placeholder="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
              <Input data-testid="wrestler-nickname-input" placeholder="Nickname" value={form.nickname} onChange={e => setForm(f => ({ ...f, nickname: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Weight Class" value={form.weight_class} onChange={e => setForm(f => ({ ...f, weight_class: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
              <Input placeholder="Hometown" value={form.hometown} onChange={e => setForm(f => ({ ...f, hometown: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            </div>
            <Textarea placeholder="Bio" value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} rows={3} className="rounded-none border-none bg-muted font-body text-sm" />
            <ImageUpload value={form.image_url} onChange={url => setForm(f => ({ ...f, image_url: url }))} token={token} />
            <Input placeholder="Or paste image URL" value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Wins" value={form.wins} onChange={e => setForm(f => ({ ...f, wins: parseInt(e.target.value) || 0 }))} className="rounded-none border-none bg-muted font-body text-sm" />
              <Input type="number" placeholder="Losses" value={form.losses} onChange={e => setForm(f => ({ ...f, losses: parseInt(e.target.value) || 0 }))} className="rounded-none border-none bg-muted font-body text-sm" />
            </div>
            <label className="flex items-center gap-2 font-body text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_champion} onChange={e => setForm(f => ({ ...f, is_champion: e.target.checked }))} />
              Champion
            </label>
            {form.is_champion && (
              <Input placeholder="Championship Title" value={form.championship_title} onChange={e => setForm(f => ({ ...f, championship_title: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            )}
            <div className="flex gap-3 pt-2">
              <Button data-testid="save-wrestler-btn" onClick={handleSave} className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 flex-1 border-none">
                <Check size={14} className="mr-1" /> {editing ? "Update" : "Save"}
              </Button>
              <Button onClick={resetForm} className="rounded-none bg-muted text-foreground hover:bg-muted/80 font-heading uppercase text-sm h-10 border-none">
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {wrestlers.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-8 text-center">No wrestlers on the roster yet.</p>
      ) : (
        <div className="space-y-2">
          {wrestlers.map(w => (
            <div key={w.id} data-testid={`admin-wrestler-${w.id}`} className="flex items-center justify-between bg-muted p-4">
              <div>
                <p className="font-heading text-base uppercase">{w.name} {w.nickname && <span className="text-primary">"{w.nickname}"</span>}</p>
                <p className="font-body text-xs text-muted-foreground">{w.hometown || "Unknown"} &middot; {w.wins}W-{w.losses}L {w.is_champion && `| ${w.championship_title}`}</p>
              </div>
              <div className="flex gap-2">
                <Button size="icon" onClick={() => startEdit(w)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-primary border-none"><Edit2 size={14} /></Button>
                <Button size="icon" onClick={() => handleDelete(w.id)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-red-800 border-none"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventsPanel({ token }) {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", date: "", venue: "", city: "", description: "", is_featured: false });

  const headers = { Authorization: `Bearer ${token}` };

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events`);
      setEvents(data.sort((a, b) => new Date(b.date + 'T12:00:00') - new Date(a.date + 'T12:00:00')));
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const resetForm = () => {
    setForm({ title: "", date: "", venue: "", city: "", description: "", is_featured: false });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.title || !form.date) { toast.error("Title and date required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/events/${editing}`, form, { headers });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, form, { headers });
        toast.success("Event created");
      }
      resetForm();
      loadEvents();
    } catch { toast.error("Save failed"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`, { headers });
      toast.success("Event deleted");
      loadEvents();
    } catch { toast.error("Delete failed"); }
  };

  const startEdit = (ev) => {
    setForm({
      title: ev.title, date: ev.date ? ev.date.split('T')[0] : "",
      venue: ev.venue || "", city: ev.city || "",
      description: ev.description || "", is_featured: ev.is_featured || false
    });
    setEditing(ev.id);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl uppercase">Events ({events.length})</h2>
        <Button
          data-testid="add-event-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 border-none"
        >
          <Plus size={14} className="mr-1" /> Add Event
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-none border-none bg-background max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl">{editing ? "Edit Event" : "Add Event"}</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Fill in the event details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input data-testid="event-title-input" placeholder="Event Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Input data-testid="event-date-input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Venue" value={form.venue} onChange={e => setForm(f => ({ ...f, venue: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
              <Input placeholder="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            </div>
            <Textarea placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="rounded-none border-none bg-muted font-body text-sm" />
            <label className="flex items-center gap-2 font-body text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} />
              Featured Event
            </label>
            <div className="flex gap-3 pt-2">
              <Button data-testid="save-event-btn" onClick={handleSave} className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 flex-1 border-none">
                <Check size={14} className="mr-1" /> {editing ? "Update" : "Save"}
              </Button>
              <Button onClick={resetForm} className="rounded-none bg-muted text-foreground hover:bg-muted/80 font-heading uppercase text-sm h-10 border-none">
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {events.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-8 text-center">No events scheduled yet.</p>
      ) : (
        <div className="space-y-2">
          {events.map(ev => (
            <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="flex items-center justify-between bg-muted p-4">
              <div>
                <p className="font-heading text-base uppercase">{ev.title} {ev.is_featured && <span className="text-primary text-xs">[FEATURED]</span>}</p>
                <p className="font-body text-xs text-muted-foreground">
                  {new Date(ev.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  {ev.venue && ` | ${ev.venue}`}{ev.city && `, ${ev.city}`}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="icon" onClick={() => startEdit(ev)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-primary border-none"><Edit2 size={14} /></Button>
                <Button size="icon" onClick={() => handleDelete(ev.id)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-red-800 border-none"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessagesPanel({ token }) {
  const [messages, setMessages] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/contact`, { headers });
      setMessages(data);
    } catch { /* empty */ }
  }, [token]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`, { headers });
      toast.success("Message deleted");
      loadMessages();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <h2 className="font-heading text-xl uppercase mb-6">Messages ({messages.length})</h2>
      {messages.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-8 text-center">No messages yet.</p>
      ) : (
        <div className="space-y-3">
          {messages.map(msg => (
            <div key={msg.id} data-testid={`admin-message-${msg.id}`} className="bg-muted p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-heading text-base uppercase">{msg.name}</p>
                  <p className="font-body text-xs text-muted-foreground">{msg.email} &middot; {new Date(msg.created_at).toLocaleDateString()}</p>
                </div>
                <Button size="icon" onClick={() => handleDelete(msg.id)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-red-800 border-none"><Trash2 size={14} /></Button>
              </div>
              {msg.subject && <p className="font-heading text-sm uppercase text-primary mb-1">{msg.subject}</p>}
              <p className="font-body text-sm">{msg.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubscribersPanel({ token }) {
  const [subs, setSubs] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const loadSubs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/subscribers`, { headers });
      setSubs(data);
    } catch { /* empty */ }
  }, [token]);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/subscribers/${id}`, { headers });
      toast.success("Subscriber removed");
      loadSubs();
    } catch { toast.error("Delete failed"); }
  };

  const handleExport = () => {
    const csv = "Email,Date Joined\n" + subs.map(s => `${s.email},${new Date(s.created_at).toLocaleDateString()}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pwc_email_list.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl uppercase">Email List ({subs.length})</h2>
        {subs.length > 0 && (
          <Button
            data-testid="export-subs-btn"
            onClick={handleExport}
            className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 border-none"
          >
            Export CSV
          </Button>
        )}
      </div>
      {subs.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-8 text-center">No subscribers yet.</p>
      ) : (
        <div className="space-y-2">
          {subs.map(s => (
            <div key={s.id} data-testid={`admin-sub-${s.id}`} className="flex items-center justify-between bg-muted p-4">
              <div>
                <p className="font-body text-sm">{s.email}</p>
                <p className="font-body text-xs text-muted-foreground">Joined {new Date(s.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
              <Button size="icon" onClick={() => handleDelete(s.id)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-red-800 border-none"><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChampionshipsPanel({ token }) {
  const [championships, setChampionships] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", image_url: "", current_holder: "",
    date_won: "", reign_number: 0, is_active: true, history: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  const loadChampionships = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/championships`);
      setChampionships(data);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadChampionships(); }, [loadChampionships]);

  const resetForm = () => {
    setForm({ name: "", description: "", image_url: "", current_holder: "",
      date_won: "", reign_number: 0, is_active: true, history: "" });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/championships/${editing}`, form, { headers });
        toast.success("Championship updated");
      } else {
        await axios.post(`${API}/championships`, form, { headers });
        toast.success("Championship added");
      }
      resetForm();
      loadChampionships();
    } catch { toast.error("Save failed"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/championships/${id}`, { headers });
      toast.success("Championship removed");
      loadChampionships();
    } catch { toast.error("Delete failed"); }
  };

  const startEdit = (c) => {
    setForm({
      name: c.name, description: c.description || "", image_url: c.image_url || "",
      current_holder: c.current_holder || "", date_won: c.date_won || "",
      reign_number: c.reign_number || 0, is_active: c.is_active !== false, history: c.history || ""
    });
    setEditing(c.id);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl uppercase">Championships ({championships.length})</h2>
        <Button
          data-testid="add-championship-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 border-none"
        >
          <Plus size={14} className="mr-1" /> Add Title
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-none border-none bg-background max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl">{editing ? "Edit Championship" : "Add Championship"}</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Manage championship details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input data-testid="champ-name-input" placeholder="Championship Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Textarea placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="rounded-none border-none bg-muted font-body text-sm" />
            <ImageUpload value={form.image_url} onChange={url => setForm(f => ({ ...f, image_url: url }))} token={token} />
            <Input placeholder="Or paste belt image URL" value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Current Holder" value={form.current_holder} onChange={e => setForm(f => ({ ...f, current_holder: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
              <Input type="date" placeholder="Date Won" value={form.date_won} onChange={e => setForm(f => ({ ...f, date_won: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            </div>
            <Input type="number" placeholder="Reign Number" value={form.reign_number} onChange={e => setForm(f => ({ ...f, reign_number: parseInt(e.target.value) || 0 }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Textarea placeholder="Title History" value={form.history} onChange={e => setForm(f => ({ ...f, history: e.target.value }))} rows={3} className="rounded-none border-none bg-muted font-body text-sm" />
            <label className="flex items-center gap-2 font-body text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              Active Championship
            </label>
            <div className="flex gap-3 pt-2">
              <Button data-testid="save-championship-btn" onClick={handleSave} className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 flex-1 border-none">
                <Check size={14} className="mr-1" /> {editing ? "Update" : "Save"}
              </Button>
              <Button onClick={resetForm} className="rounded-none bg-muted text-foreground hover:bg-muted/80 font-heading uppercase text-sm h-10 border-none">
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {championships.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-8 text-center">No championships yet.</p>
      ) : (
        <div className="space-y-2">
          {championships.map(c => (
            <div key={c.id} data-testid={`admin-championship-${c.id}`} className="flex items-center justify-between bg-muted p-4">
              <div className="flex items-center gap-4">
                {c.image_url && <img src={c.image_url} alt={c.name} className="h-12 w-16 object-contain bg-foreground p-1" />}
                <div>
                  <p className="font-heading text-base uppercase">{c.name}</p>
                  <p className="font-body text-xs text-muted-foreground">
                    Holder: {c.current_holder || "Vacant"} {!c.is_active && "| INACTIVE"}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="icon" onClick={() => startEdit(c)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-primary border-none"><Edit2 size={14} /></Button>
                <Button size="icon" onClick={() => handleDelete(c.id)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-red-800 border-none"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewsPanel({ token }) {
  const [articles, setArticles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", image_url: "", is_featured: false });

  const headers = { Authorization: `Bearer ${token}` };

  const loadNews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/news`);
      setArticles(data);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadNews(); }, [loadNews]);

  const resetForm = () => {
    setForm({ title: "", body: "", image_url: "", is_featured: false });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.title || !form.body) { toast.error("Title and body required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/news/${editing}`, form, { headers });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/news`, form, { headers });
        toast.success("Article published");
      }
      resetForm();
      loadNews();
    } catch { toast.error("Save failed"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/news/${id}`, { headers });
      toast.success("Article deleted");
      loadNews();
    } catch { toast.error("Delete failed"); }
  };

  const startEdit = (a) => {
    setForm({
      title: a.title, body: a.body, image_url: a.image_url || "",
      is_featured: a.is_featured || false
    });
    setEditing(a.id);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl uppercase">News ({articles.length})</h2>
        <Button
          data-testid="add-news-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 border-none"
        >
          <Plus size={14} className="mr-1" /> Add Article
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-none border-none bg-background max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl">{editing ? "Edit Article" : "New Article"}</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Write your news article.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input data-testid="news-title-input" placeholder="Headline *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Textarea data-testid="news-body-input" placeholder="Article body *" value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={8} className="rounded-none border-none bg-muted font-body text-sm" />
            <ImageUpload value={form.image_url} onChange={url => setForm(f => ({ ...f, image_url: url }))} token={token} />
            <Input placeholder="Or paste image URL" value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <label className="flex items-center gap-2 font-body text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} />
              Featured Article (lead story)
            </label>
            <div className="flex gap-3 pt-2">
              <Button data-testid="save-news-btn" onClick={handleSave} className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 flex-1 border-none">
                <Check size={14} className="mr-1" /> {editing ? "Update" : "Publish"}
              </Button>
              <Button onClick={resetForm} className="rounded-none bg-muted text-foreground hover:bg-muted/80 font-heading uppercase text-sm h-10 border-none">
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {articles.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-8 text-center">No articles yet.</p>
      ) : (
        <div className="space-y-2">
          {articles.map(a => (
            <div key={a.id} data-testid={`admin-news-${a.id}`} className="flex items-center justify-between bg-muted p-4">
              <div>
                <p className="font-heading text-base uppercase">{a.title} {a.is_featured && <span className="text-primary text-xs">[FEATURED]</span>}</p>
                <p className="font-body text-xs text-muted-foreground">
                  {new Date(a.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  {a.image_url && ' | Has image'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="icon" onClick={() => startEdit(a)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-primary border-none"><Edit2 size={14} /></Button>
                <Button size="icon" onClick={() => handleDelete(a.id)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-red-800 border-none"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HomepagePanel({ token }) {
  const [flyerUrl, setFlyerUrl] = useState("");
  const [flyerLink, setFlyerLink] = useState("");
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => {
      setFlyerUrl(r.data?.flyer_url || "");
      setFlyerLink(r.data?.flyer_link || "");
    }).catch(() => {});
    axios.get(`${API}/stats`, { headers: { Authorization: `Bearer ${token}` } }).then(r => {
      setStats(r.data);
    }).catch(() => {});
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, { flyer_url: flyerUrl, flyer_link: flyerLink }, { headers });
      toast.success("Homepage updated!");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, { flyer_url: "", flyer_link: "" }, { headers });
      setFlyerUrl("");
      setFlyerLink("");
      toast.success("Flyer removed from homepage");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {/* Visitor Stats */}
      {stats && (
        <div className="mb-10">
          <h2 className="font-heading text-xl uppercase mb-4">Site Stats</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            <div className="bg-foreground p-4 text-center">
              <p className="font-heading text-2xl text-[#B09030]">{stats.total_visitors}</p>
              <p className="font-body text-[9px] text-background/50 uppercase tracking-wider">Total Visitors</p>
            </div>
            <div className="bg-foreground p-4 text-center">
              <p className="font-heading text-2xl text-[#B09030]">{stats.today_visitors}</p>
              <p className="font-body text-[9px] text-background/50 uppercase tracking-wider">Today</p>
            </div>
            <div className="bg-foreground p-4 text-center">
              <p className="font-heading text-2xl text-[#B09030]">{stats.total_page_views}</p>
              <p className="font-body text-[9px] text-background/50 uppercase tracking-wider">Page Views</p>
            </div>
            <div className="bg-foreground p-4 text-center">
              <p className="font-heading text-2xl text-[#B09030]">{stats.total_subscribers}</p>
              <p className="font-body text-[9px] text-background/50 uppercase tracking-wider">Email List</p>
            </div>
            <div className="bg-foreground p-4 text-center">
              <p className="font-heading text-2xl text-[#B09030]">{stats.total_contacts}</p>
              <p className="font-body text-[9px] text-background/50 uppercase tracking-wider">Messages</p>
            </div>
          </div>
          {/* 7-day chart */}
          <p className="font-heading text-sm uppercase mb-3">Last 7 Days</p>
          <div className="flex items-end gap-2 h-24">
            {stats.daily?.slice().reverse().map(d => {
              const max = Math.max(...stats.daily.map(x => x.visitors), 1);
              const height = Math.max((d.visitors / max) * 100, 4);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <span className="font-body text-[9px] text-foreground/60">{d.visitors}</span>
                  <div className="w-full bg-[#B09030]" style={{ height: `${height}%` }} />
                  <span className="font-body text-[8px] text-muted-foreground">
                    {new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="h-[1px] bg-foreground/10 mt-6 mb-6" />
        </div>
      )}

      <h2 className="font-heading text-xl uppercase mb-6">Homepage Flyer</h2>
      <p className="font-body text-sm text-muted-foreground mb-6">
        Upload a show flyer or promo image. It will display prominently at the top of the homepage for visitors to see immediately.
      </p>

      <div className="space-y-4 max-w-lg">
        <div>
          <label className="font-heading text-sm uppercase block mb-2">Flyer Image</label>
          <ImageUpload value={flyerUrl} onChange={url => setFlyerUrl(url)} token={token} />
          <Input
            placeholder="Or paste image URL"
            value={flyerUrl}
            onChange={e => setFlyerUrl(e.target.value)}
            className="rounded-none border-none bg-muted font-body text-sm mt-2"
          />
        </div>

        <div>
          <label className="font-heading text-sm uppercase block mb-2">Link URL (optional)</label>
          <Input
            data-testid="flyer-link-input"
            placeholder="https://... (click flyer goes here)"
            value={flyerLink}
            onChange={e => setFlyerLink(e.target.value)}
            className="rounded-none border-none bg-muted font-body text-sm"
          />
          <p className="font-body text-[10px] text-muted-foreground mt-1">If set, clicking the flyer will open this link (e.g. ticket page).</p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            data-testid="save-flyer-btn"
            onClick={handleSave}
            disabled={saving}
            className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 border-none"
          >
            <Check size={14} className="mr-1" /> {saving ? "Saving..." : "Save to Homepage"}
          </Button>
          {flyerUrl && (
            <Button
              data-testid="clear-flyer-btn"
              onClick={handleClear}
              disabled={saving}
              className="rounded-none bg-muted text-foreground hover:bg-red-800 hover:text-background font-heading uppercase text-sm h-10 border-none"
            >
              <Trash2 size={14} className="mr-1" /> Remove Flyer
            </Button>
          )}
        </div>
      </div>

      {/* Preview */}
      {flyerUrl && (
        <div className="mt-8">
          <p className="font-heading text-sm uppercase mb-3">Preview</p>
          <div className="max-w-md bg-muted p-4">
            <img src={flyerUrl} alt="Flyer preview" className="w-full h-auto" />
          </div>
        </div>
      )}
    </div>
  );
}

function SponsorsPanel({ token }) {
  const [sponsors, setSponsors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", logo_url: "", website_url: "", tier: "standard" });

  const headers = { Authorization: `Bearer ${token}` };

  const loadSponsors = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sponsors`);
      setSponsors(data);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadSponsors(); }, [loadSponsors]);

  const resetForm = () => {
    setForm({ name: "", logo_url: "", website_url: "", tier: "standard" });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/sponsors/${editing}`, form, { headers });
        toast.success("Sponsor updated");
      } else {
        await axios.post(`${API}/sponsors`, form, { headers });
        toast.success("Sponsor added");
      }
      resetForm();
      loadSponsors();
    } catch { toast.error("Save failed"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/sponsors/${id}`, { headers });
      toast.success("Sponsor removed");
      loadSponsors();
    } catch { toast.error("Delete failed"); }
  };

  const startEdit = (s) => {
    setForm({ name: s.name, logo_url: s.logo_url || "", website_url: s.website_url || "", tier: s.tier || "standard" });
    setEditing(s.id);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl uppercase">Sponsors ({sponsors.length})</h2>
        <Button
          data-testid="add-sponsor-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 border-none"
        >
          <Plus size={14} className="mr-1" /> Add Sponsor
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-none border-none bg-background max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl">{editing ? "Edit Sponsor" : "Add Sponsor"}</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Add sponsor details and logo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input data-testid="sponsor-name-input" placeholder="Sponsor Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <ImageUpload value={form.logo_url} onChange={url => setForm(f => ({ ...f, logo_url: url }))} token={token} />
            <Input placeholder="Or paste logo URL" value={form.logo_url} onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Input placeholder="Website URL (optional)" value={form.website_url} onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <select value={form.tier} onChange={e => setForm(f => ({ ...f, tier: e.target.value }))} className="w-full rounded-none border-none bg-muted font-body text-sm px-4 py-3 text-foreground">
              <option value="standard">Standard</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
              <option value="presenting">Presenting</option>
            </select>
            <div className="flex gap-3 pt-2">
              <Button data-testid="save-sponsor-btn" onClick={handleSave} className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 flex-1 border-none">
                <Check size={14} className="mr-1" /> {editing ? "Update" : "Save"}
              </Button>
              <Button onClick={resetForm} className="rounded-none bg-muted text-foreground hover:bg-muted/80 font-heading uppercase text-sm h-10 border-none">
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {sponsors.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-8 text-center">No sponsors yet.</p>
      ) : (
        <div className="space-y-2">
          {sponsors.map(s => (
            <div key={s.id} data-testid={`admin-sponsor-${s.id}`} className="flex items-center justify-between bg-muted p-4">
              <div className="flex items-center gap-4">
                {s.logo_url && <img src={s.logo_url} alt={s.name} className="h-10 w-14 object-contain bg-foreground p-1" />}
                <div>
                  <p className="font-heading text-base uppercase">{s.name} <span className="text-primary text-xs">[{s.tier?.toUpperCase()}]</span></p>
                  {s.website_url && <p className="font-body text-xs text-muted-foreground">{s.website_url}</p>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="icon" onClick={() => startEdit(s)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-primary border-none"><Edit2 size={14} /></Button>
                <Button size="icon" onClick={() => handleDelete(s.id)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-red-800 border-none"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryPanel({ token }) {
  const [photos, setPhotos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ image_url: "", caption: "", album: "General" });

  const headers = { Authorization: `Bearer ${token}` };

  const loadPhotos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/gallery`);
      setPhotos(data);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  const resetForm = () => {
    setForm({ image_url: "", caption: "", album: "General" });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.image_url) { toast.error("Upload an image first"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/gallery/${editing}`, form, { headers });
        toast.success("Photo updated");
      } else {
        await axios.post(`${API}/gallery`, form, { headers });
        toast.success("Photo added to gallery");
      }
      resetForm();
      loadPhotos();
    } catch { toast.error("Save failed"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers });
      toast.success("Photo removed");
      loadPhotos();
    } catch { toast.error("Delete failed"); }
  };

  const startEdit = (p) => {
    setForm({ image_url: p.image_url || "", caption: p.caption || "", album: p.album || "General" });
    setEditing(p.id);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl uppercase">Gallery ({photos.length})</h2>
        <Button
          data-testid="add-photo-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 border-none"
        >
          <Plus size={14} className="mr-1" /> Add Photo
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-none border-none bg-background max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl">{editing ? "Edit Photo" : "Add Photo"}</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Upload a photo to the gallery.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <ImageUpload value={form.image_url} onChange={url => setForm(f => ({ ...f, image_url: url }))} token={token} />
            <Input placeholder="Or paste image URL" value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Input data-testid="photo-caption-input" placeholder="Caption (optional)" value={form.caption} onChange={e => setForm(f => ({ ...f, caption: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Input data-testid="photo-album-input" placeholder="Album name (e.g. Return to Tradition 2026)" value={form.album} onChange={e => setForm(f => ({ ...f, album: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <div className="flex gap-3 pt-2">
              <Button data-testid="save-photo-btn" onClick={handleSave} className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 flex-1 border-none">
                <Check size={14} className="mr-1" /> {editing ? "Update" : "Add to Gallery"}
              </Button>
              <Button onClick={resetForm} className="rounded-none bg-muted text-foreground hover:bg-muted/80 font-heading uppercase text-sm h-10 border-none">
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {photos.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-8 text-center">No photos in the gallery yet.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {photos.map(p => (
            <div key={p.id} data-testid={`admin-photo-${p.id}`} className="relative group aspect-square bg-foreground">
              <img src={p.image_url} alt={p.caption || ""} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <Button size="icon" onClick={() => startEdit(p)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-primary border-none"><Edit2 size={14} /></Button>
                <Button size="icon" onClick={() => handleDelete(p.id)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-red-800 border-none"><Trash2 size={14} /></Button>
              </div>
              {p.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                  <p className="font-body text-[9px] text-[#E8DFC8] truncate">{p.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductsPanel({ token }) {
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price: "", image_url: "", category: "General", sizes: "", in_stock: true });

  const headers = { Authorization: `Bearer ${token}` };

  const loadProducts = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/products`); setProducts(data); } catch { /* empty */ }
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const resetForm = () => {
    setForm({ name: "", description: "", price: "", image_url: "", category: "General", sizes: "", in_stock: true });
    setEditing(null); setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name || !form.price || parseFloat(form.price) <= 0) { toast.error("Name and price required"); return; }
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editing) { await axios.put(`${API}/products/${editing}`, payload, { headers }); toast.success("Product updated"); }
      else { await axios.post(`${API}/products`, payload, { headers }); toast.success("Product added"); }
      resetForm(); loadProducts();
    } catch { toast.error("Save failed"); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/products/${id}`, { headers }); toast.success("Product removed"); loadProducts(); }
    catch { toast.error("Delete failed"); }
  };

  const startEdit = (p) => {
    setForm({ name: p.name, description: p.description || "", price: String(p.price), image_url: p.image_url || "", category: p.category || "General", sizes: p.sizes || "", in_stock: p.in_stock !== false });
    setEditing(p.id); setShowForm(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl uppercase">Products ({products.length})</h2>
        <Button data-testid="add-product-btn" onClick={() => { resetForm(); setShowForm(true); }} className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 border-none">
          <Plus size={14} className="mr-1" /> Add Product
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-none border-none bg-background max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl">{editing ? "Edit Product" : "Add Product"}</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Add merch item details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input data-testid="product-name-input" placeholder="Product Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Input type="number" step="0.01" placeholder="Price *" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Textarea placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="rounded-none border-none bg-muted font-body text-sm" />
            <ImageUpload value={form.image_url} onChange={url => setForm(f => ({ ...f, image_url: url }))} token={token} />
            <Input placeholder="Or paste image URL" value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Input placeholder="Category (e.g. T-Shirts, Posters)" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <Input placeholder="Sizes (comma separated: S, M, L, XL, 2XL)" value={form.sizes} onChange={e => setForm(f => ({ ...f, sizes: e.target.value }))} className="rounded-none border-none bg-muted font-body text-sm" />
            <label className="flex items-center gap-2 font-body text-sm cursor-pointer">
              <input type="checkbox" checked={form.in_stock} onChange={e => setForm(f => ({ ...f, in_stock: e.target.checked }))} />
              In Stock
            </label>
            <div className="flex gap-3 pt-2">
              <Button data-testid="save-product-btn" onClick={handleSave} className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-sm h-10 flex-1 border-none">
                <Check size={14} className="mr-1" /> {editing ? "Update" : "Save"}
              </Button>
              <Button onClick={resetForm} className="rounded-none bg-muted text-foreground hover:bg-muted/80 font-heading uppercase text-sm h-10 border-none">
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {products.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-8 text-center">No products yet.</p>
      ) : (
        <div className="space-y-2">
          {products.map(p => (
            <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex items-center justify-between bg-muted p-4">
              <div className="flex items-center gap-4">
                {p.image_url && <img src={p.image_url} alt={p.name} className="h-12 w-12 object-cover bg-foreground" />}
                <div>
                  <p className="font-heading text-base uppercase">{p.name} <span className="text-primary">${p.price.toFixed(2)}</span></p>
                  <p className="font-body text-xs text-muted-foreground">{p.category}{p.sizes && ` | Sizes: ${p.sizes}`}{!p.in_stock && ' | OUT OF STOCK'}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="icon" onClick={() => startEdit(p)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-primary border-none"><Edit2 size={14} /></Button>
                <Button size="icon" onClick={() => handleDelete(p.id)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-red-800 border-none"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrdersPanel({ token }) {
  const [orders, setOrders] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const loadOrders = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/orders`, { headers }); setOrders(data); } catch { /* empty */ }
  }, [token]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const updateStatus = async (id, status, payment_status) => {
    try {
      await axios.put(`${API}/orders/${id}/status`, { status, payment_status }, { headers });
      toast.success("Order updated");
      loadOrders();
    } catch { toast.error("Update failed"); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/orders/${id}`, { headers }); toast.success("Order deleted"); loadOrders(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <h2 className="font-heading text-xl uppercase mb-6">Orders ({orders.length})</h2>
      {orders.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-8 text-center">No orders yet.</p>
      ) : (
        <div className="space-y-3">
          {orders.map(o => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-muted p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-heading text-base uppercase">{o.customer_name}</p>
                  <p className="font-body text-xs text-muted-foreground">{o.customer_email}</p>
                  <p className="font-body text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                </div>
                <div className="text-right">
                  <p className="font-heading text-lg text-primary">${o.total?.toFixed(2)}</p>
                  <div className="flex gap-2 mt-1">
                    <span className={`font-body text-[10px] uppercase px-2 py-1 ${o.status === 'shipped' ? 'bg-green-800 text-white' : o.status === 'confirmed' ? 'bg-[#B09030] text-black' : 'bg-foreground/20'}`}>
                      {o.status}
                    </span>
                    <span className={`font-body text-[10px] uppercase px-2 py-1 ${o.payment_status === 'paid' ? 'bg-green-800 text-white' : 'bg-[#8B3020] text-white'}`}>
                      {o.payment_status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-background p-3 mb-3">
                <p className="font-body text-xs text-muted-foreground uppercase tracking-wider mb-2">Items</p>
                {o.items?.map((item, idx) => (
                  <p key={idx} className="font-body text-sm">{item.name} {item.size && `(${item.size})`} x{item.quantity} — ${(item.price * item.quantity).toFixed(2)}</p>
                ))}
                <p className="font-body text-xs text-muted-foreground uppercase tracking-wider mt-3 mb-1">Ship To</p>
                <p className="font-body text-sm">{o.shipping_address}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => updateStatus(o.id, 'confirmed', o.payment_status)} className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase text-xs h-8 border-none">Confirm</Button>
                <Button size="sm" onClick={() => updateStatus(o.id, o.status, 'paid')} className="rounded-none bg-green-800 text-white hover:bg-green-700 font-heading uppercase text-xs h-8 border-none">Mark Paid</Button>
                <Button size="sm" onClick={() => updateStatus(o.id, 'shipped', o.payment_status)} className="rounded-none bg-[#B09030] text-black hover:bg-[#B09030]/80 font-heading uppercase text-xs h-8 border-none">Shipped</Button>
                <Button size="icon" onClick={() => handleDelete(o.id)} className="rounded-none h-8 w-8 bg-foreground text-background hover:bg-red-800 border-none ml-auto"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("pwc_token") || "");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUser(r.data))
        .catch(() => { localStorage.removeItem("pwc_token"); setToken(""); })
        .finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, [token]);

  const handleLogin = (userData) => {
    setUser(userData);
    setToken(localStorage.getItem("pwc_token"));
  };

  const handleLogout = () => {
    localStorage.removeItem("pwc_token");
    setUser(null);
    setToken("");
  };

  if (checking) {
    return <div className="min-h-screen bg-foreground flex items-center justify-center">
      <p className="font-heading text-background uppercase">Loading...</p>
    </div>;
  }

  if (!user) {
    return (
      <>
        <Toaster position="top-right" toastOptions={{ style: { background: '#EFE8D8', color: '#000', border: 'none', borderRadius: '0', fontFamily: '"Courier Prime", monospace' } }} />
        <AdminLogin onLogin={handleLogin} />
      </>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background">
      <Toaster position="top-right" toastOptions={{ style: { background: '#000', color: '#EFE8D8', border: 'none', borderRadius: '0', fontFamily: '"Courier Prime", monospace' } }} />

      {/* Admin header */}
      <header className="bg-foreground text-background py-4 px-4 sm:px-6 border-b-4 border-primary">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={PWC_LOGO} alt="PWC" className="h-10 invert" />
            <div>
              <h1 className="font-heading text-lg uppercase text-background">Front Office</h1>
              <p className="font-body text-xs text-background/50">{user.email}</p>
            </div>
          </div>
          <Button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="rounded-none bg-background/10 text-background hover:bg-background/20 font-heading uppercase text-sm h-9 border-none"
          >
            <LogOut size={14} className="mr-1" /> Logout
          </Button>
        </div>
      </header>

      {/* Dashboard */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="wrestlers" className="w-full">
          <TabsList data-testid="admin-tabs" className="bg-muted rounded-none h-auto p-0 w-full flex flex-wrap">
            <TabsTrigger value="homepage" data-testid="tab-homepage" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Image size={16} className="mr-2" /> Homepage
            </TabsTrigger>
            <TabsTrigger value="wrestlers" data-testid="tab-wrestlers" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Users size={16} className="mr-2" /> Roster
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <CalendarDays size={16} className="mr-2" /> Events
            </TabsTrigger>
            <TabsTrigger value="championships" data-testid="tab-championships" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Trophy size={16} className="mr-2" /> Titles
            </TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Newspaper size={16} className="mr-2" /> News
            </TabsTrigger>
            <TabsTrigger value="sponsors" data-testid="tab-sponsors" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Handshake size={16} className="mr-2" /> Sponsors
            </TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Camera size={16} className="mr-2" /> Gallery
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <ShoppingBag size={16} className="mr-2" /> Merch
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Package size={16} className="mr-2" /> Orders
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Mail size={16} className="mr-2" /> Messages
            </TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subscribers" className="rounded-none flex-1 py-3 font-heading uppercase text-sm data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Mail size={16} className="mr-2" /> Email List
            </TabsTrigger>
          </TabsList>
          <TabsContent value="homepage" className="mt-6">
            <HomepagePanel token={token} />
          </TabsContent>
          <TabsContent value="wrestlers" className="mt-6">
            <WrestlersPanel token={token} />
          </TabsContent>
          <TabsContent value="events" className="mt-6">
            <EventsPanel token={token} />
          </TabsContent>
          <TabsContent value="championships" className="mt-6">
            <ChampionshipsPanel token={token} />
          </TabsContent>
          <TabsContent value="news" className="mt-6">
            <NewsPanel token={token} />
          </TabsContent>
          <TabsContent value="sponsors" className="mt-6">
            <SponsorsPanel token={token} />
          </TabsContent>
          <TabsContent value="gallery" className="mt-6">
            <GalleryPanel token={token} />
          </TabsContent>
          <TabsContent value="products" className="mt-6">
            <ProductsPanel token={token} />
          </TabsContent>
          <TabsContent value="orders" className="mt-6">
            <OrdersPanel token={token} />
          </TabsContent>
          <TabsContent value="messages" className="mt-6">
            <MessagesPanel token={token} />
          </TabsContent>
          <TabsContent value="subscribers" className="mt-6">
            <SubscribersPanel token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
